import { useState, useRef, useEffect, useCallback } from 'react';
import { BsSearch } from 'react-icons/bs';
import { AiOutlineClose } from 'react-icons/ai';
import styled from 'styled-components';

interface SearchBarProps {
  placeholder?: string;
  query: string;
  setQuery: (value: string) => void;
  collapsible?: boolean;
  disabled?: boolean;
}

const SearchBar = ({ query, setQuery, placeholder, collapsible = false, disabled }: SearchBarProps) => {
  const [barOpened, setBarOpened] = useState<boolean>(false);

  const inputFocus = useRef<HTMLInputElement>(null);
  const divRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback(
    event => {
      if (divRef.current && !divRef.current.contains(event.target)) {
        if (barOpened) {
          setBarOpened(false);
        }
      }
    },
    [barOpened]
  );

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [handleClickOutside]);

  return (
    <Container
      barOpened={barOpened}
      collapsible={collapsible}
      onClick={e => {
        setBarOpened(true);
        inputFocus?.current?.focus();
        handleClickOutside(e);
      }}
      onFocus={() => {
        setBarOpened(true);
        inputFocus?.current?.focus();
      }}
      ref={divRef}>
      <IconContainer barOpened={barOpened} collapsible={collapsible}>
        <BsSearch />
      </IconContainer>
      <InputContainer barOpened={barOpened} collapsible={collapsible}>
        <Input
          onChange={e => setQuery(e.target.value)}
          ref={inputFocus}
          value={query}
          barOpened={barOpened}
          placeholder={placeholder || 'Rechercher ...'}
          disabled={disabled}
        />
        <AiOutlineClose
          onClick={() => {
            !disabled && setQuery('');
            !disabled && inputFocus?.current?.focus();
          }}
        />
      </InputContainer>
    </Container>
  );
};

const Container = styled.div<{ collapsible?: boolean; barOpened?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  background-color: #f3f4f6;
  padding: 1rem;
  height: 2.4rem;
  border-radius: 5px;
  transition: width 300ms cubic-bezier(0.645, 0.045, 0.355, 1);

  ${({ collapsible, barOpened }) => {
    if (collapsible) {
      return `
        width: ${barOpened ? '25rem' : '1.5rem'};
        cursor: ${barOpened ? 'auto' : 'pointer'};
      `;
    } else {
      return `
        width: 25rem;
      `;
    }
  }}
`;

const IconContainer = styled.div<{ collapsible?: boolean; barOpened?: boolean }>`
  line-height: 1;
  pointer-events: ${({ collapsible, barOpened }) => (collapsible && barOpened ? 'auto' : 'none')};
  cursor: ${({ collapsible, barOpened }) => (collapsible && barOpened ? 'pointer' : 'none')};
  background-color: transparent;
  border: none;
  outline: none;
  color: white;
  svg {
    color: black;
    font-size: 1.5rem;
  }
`;

const InputContainer = styled.div<{ collapsible?: boolean; barOpened?: boolean }>`
  display: flex;
  justify-content: space-around;
  width: 100%;
  align-items: center;
  svg {
    color: black;
    cursor: pointer;
    font-size: 1.5rem;

    ${({ collapsible, barOpened }) => {
      if (collapsible) {
        return `
          display: ${barOpened ? 'block' : 'none'};
        `;
      } else {
        return `
          display: block;
        `;
      }
    }}
  }
`;

const Input = styled.input<{ collapsible?: boolean; barOpened?: boolean }>`
  font-size: 14px;
  line-height: 1;
  background-color: transparent;
  width: 100%;
  border: none;
  color: black;
  transition: margin 300ms cubic-bezier(0.645, 0.045, 0.355, 1);

  &:focus,
  &:active {
    outline: none;
  }
  &::placeholder {
    color: grey;
  }

  ${({ collapsible, barOpened }) => {
    if (collapsible) {
      return `
          margin-left: ${barOpened ? '1rem' : ''};
        `;
    } else {
      return `
          margin-left: 1rem;
        `;
    }
  }}
`;

export default SearchBar;
